import React from "react";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";
import styles from "./AchievementsAtElchemy.module.scss";
import {
  COUNTRIES_EXPORTED_COUNT,
  INDUSTRY_COUNT,
  PORTS_EXPORTED_COUNT,
  TOTAL_QUANTITY_SHIPPED,
} from "@/constastants/Constant";

// Figures come from the shared constants, never literals — the homepage's
// EdgeSection counts up to the same four numbers, and hardcoding them here would
// let the two sections drift apart the next time a figure is revised.
//
// `start` mirrors the homepage's `countDif`: the count begins partway up rather
// than at zero, so a small figure like 8 still animates visibly.
const cardData = [
  {
    id: 1,
    number: INDUSTRY_COUNT,
    start: 4,
    subtitle: "Industries",
    suffix: "+",
  },
  {
    id: 2,
    number: COUNTRIES_EXPORTED_COUNT,
    start: 16,
    subtitle: "Countries",
    suffix: "+",
  },
  {
    id: 3,
    number: TOTAL_QUANTITY_SHIPPED,
    // Starts at 90% of the target. The other three cover single- or double-digit
    // ranges, so at an equal duration this one had to sweep ~9,000 values and
    // read as much slower even though it finished at the same moment.
    start: 9000,
    subtitle: "MT Shipped",
    suffix: "+",
  },
  {
    id: 4,
    number: PORTS_EXPORTED_COUNT,
    start: 5,
    subtitle: "Ports",
    suffix: "+",
  },
];

/**
 * Fires CountUp's imperative `start()` the first time the band enters the
 * viewport. CountUp's render-prop form exposes `start` rather than reacting to a
 * prop, so the trigger has to live in an effect — and the ran-once ref keeps a
 * second scroll past the section from replaying the count.
 */
const CountUpTrigger = ({ inView, start, children }) => {
  const hasRun = React.useRef(false);

  React.useEffect(() => {
    if (inView && !hasRun.current) {
      hasRun.current = true;
      start();
    }
  }, [inView, start]);

  return children;
};

const AchievementCard = ({ number, start, subtitle, suffix, inView }) => {
  const isNumeric = typeof number === "number";

  return (
    <div className={styles.achievementCard}>
      {/* Number Flip */}
      <div className={styles.numberContainer}>
        {/* Grouped with thousands separators so 10000 reads as "10,000+".
            en-US is pinned rather than using the visitor's locale: this figure
            is rendered on the server and would otherwise mismatch the client's
            formatting and trigger a hydration warning.

            The pre-animation markup is the FINAL figure, not the start value, so
            the server-rendered HTML matches what a client with JS disabled — or
            a crawler — sees. CountUp only takes over once the band scrolls into
            view, and `preserveValue` keeps the final number on screen after. */}
        {isNumeric ? (
          <CountUp
            start={start}
            end={number}
            duration={1.5}
            separator=","
            preserveValue
            useEasing
            // Held until the band scrolls into view, then run once. Without this
            // the count would finish while the section was still below the fold.
            startOnMount={false}
            redraw={false}
          >
            {({ countUpRef, start: startCount }) => (
              <CountUpTrigger inView={inView} start={startCount}>
                <span className={styles.number} ref={countUpRef}>
                  {number.toLocaleString("en-US")}
                </span>
              </CountUpTrigger>
            )}
          </CountUp>
        ) : (
          <span className={styles.number}>{number}</span>
        )}
        {suffix === "K+" ? (
          <>
            <span className={styles.suffixK}>K</span>
            <span className={styles.suffix}>+</span>
          </>
        ) : (
          <span className={styles.suffix}>{suffix}</span>
        )}
      </div>
      {/* Subtitle */}
      <p className={styles.subtitle}>{subtitle}</p>
    </div>
  );
};

function AchievementsAtElchemy() {
  // One observer for the whole band, so all four figures count up together
  // rather than each starting as its own card crosses the fold.
  // `triggerOnce` — the count is a first-impression flourish, not something to
  // replay every time the user scrolls back up.
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.25 });

  return (
    <div className={styles.achievementsAtElchemy} ref={ref}>
      {/* Big Heading — one text node, no per-breakpoint duplicate spans. The
          desktop two-line wrap comes from the heading's fixed 304px column. */}
      <h2>Achievements At Elchemy</h2>
      {/* Right Section */}
      <div className={styles.rightSection}>
        {/* Row 1 */}
        <div className={styles.row}>
          <AchievementCard {...cardData[0]} inView={inView} />
          <AchievementCard {...cardData[1]} inView={inView} />
        </div>
        {/* Row 2 */}
        <div className={styles.row}>
          <AchievementCard {...cardData[2]} inView={inView} />
          <AchievementCard {...cardData[3]} inView={inView} />
        </div>
      </div>
    </div>
  );
}

export default AchievementsAtElchemy;
